import moment from "moment-timezone";
import { TaskingAppShape } from "./types";
import { compareTimestampOrNull, isValidDate } from "@eave/shared/utils/date";
import { formatDateTimeString } from "@eave/shared/utils/date-formatter";
import { TaskShape } from "@eave/shared/types/api";

const styles = require("./styles.module.scss");

/*
 * Enums
 */

// Used for comparing for purposes of cond'l rendering, and to generate unique scrollTo links
export const QueueNames = {
  InitialDisclosurePackage: "initial_disclosure_package",
  ClosingPreparations: "closing_preparations",
  ServicingTransfer: "servicing_transfer",
  PostClosingPreparation: "post_closing_preparation",
  PreliminaryClosingDisclosure: "preliminary__closing_disclosure",
  RevisedLoanEstimate: "revised_loan_estimate",
  PropertyDocFollowUp: "property_doc_follow_up",
  PendingTrailingDocs: "pending_trailing_docs",
  OrderAppraisal: "order_appraisal",
};

export enum QueueColumnNames {
  AssignedUnderwriterName = "assigned_underwriter_name",
  AssignedLoanManagerName = "assigned_loan_manager_name",
  ApplicationDate = "application_date",
  AllTrailingDocsDueBy = "all_trailing_docs_due_by",
  AllPropertyDocsDueBy = "all_property_docs_due_by",
  AppraisalApprovedDate = "appraisal_approved",
  AppraisalRequestedDate = "appraisal_requested_date",
  AppraisalReceivedDate = "appraisal_received",
  AppraisalDeliveryDueBy = "appraisal_delivery_due_by",
  AppraisalSentDate = "appraisal_sent",
  Borrower1Name = "borrower1_name",
  BorrowerEstimatedLoanAmount = "borrower_estimated_loan_amount",
  BorrowerEstimatedPurchasePrice = "borrower_estimated_purchase_price",
  CashCloseType = "cash_close_type",
  ClosingPackageDueBy = "closing_package_due_by",
  ConditionResponseDue = "condition_response_due",
  ConditionsPreReadyToReview = "conditions_pre_ready_to_review",
  ConditionsReadyToReview = "conditions_ready_to_review",
  CreditScore = "credit_score",
  CreditErrorMessages = "credit_error_messages",
  CreditReportDate = "credit_report_date",
  ClosingDate = "closing_date",
  DisbursementDate = "disbursement_date",
  InitialCDDueBy = "initial_cd_due_by",
  InitialLEDueBy = "initial_le_due_by",
  InitialLESent = "initial_le_sent",
  IntentToProceedReceipt = "intent_to_proceed_receipt",
  IsArchived = "is_archived",
  LeadNumber = "lead_number",
  LoanID = "loan_id",
  LoanApplicationID = "loan_application_id",
  LoanDecision = "loan_decision",
  LoanDecisionDate = "loan_decision_date",
  LoanStatus = "loan_status",
  LoanStatusDate = "loan_status_date",
  MessageResponseDue = "message_response_due",
  MessagesToReview = "messages_to_review",
  NOIAFollowup = "noia_followup",
  NOIADue = "noia_due_on",
  NOIATimestamp = "noia_timestamp",
  OutstandingDocuments = "outstanding_documents",
  PostClosingPrepDueBy = "post_closing_prep_due",
  PreAppAANDue = "pre_app_aan_due",
  PreAppAANReason = "pre_app_aan_reason",
  PreAppAANSent = "pre_app_aan_sent",
  PreAppUWDue = "pre_app_underwrite_due",
  PropertyState = "property_state",

  // a count of property doc conditions that do not have a received date in the system.
  PropertyDocOutstandingConditionsCount = "property_doc_outstanding_conditions_count",

  RegistrationDate = "registration_date",
  RevisedLEDueBy = "revised_le_due",
  RevisedLESent = "revised_le_sent",
  RegBCreditDecisionDue = "reg_b_credit_decision_due_by",
  ServicingTransferCompleted = "servicing_transfer_completed",
  ServicingTransferDueBy = "servicing_transfer_due_by",
  UWTaskCount = "uw_task_count",
  UWTaskFirstDueBy = "uw_task_first_due",
}

// Rewrites a snake-cased key to a prettified custom output
export const getHeadingNameFromKey = (k: string) => {
  const tableColNames = {
    [QueueColumnNames.AssignedUnderwriterName]: "Assigned Underwriter",
    [QueueColumnNames.ApplicationDate]: "Application Date",
    [QueueColumnNames.AppraisalReceivedDate]: "Appraisal Receipt Date",
    [QueueColumnNames.AppraisalRequestedDate]: "Appraisal Requested Date",
    [QueueColumnNames.AppraisalDeliveryDueBy]: "Appraisal Delivery Due",
    [QueueColumnNames.AllPropertyDocsDueBy]: "All Property Docs Due",
    [QueueColumnNames.AllTrailingDocsDueBy]: "All Trailing Docs Due",
    [QueueColumnNames.Borrower1Name]: "Borrower 1 Name",
    [QueueColumnNames.BorrowerEstimatedLoanAmount]: "Borrower Estimated Loan Amount",
    [QueueColumnNames.BorrowerEstimatedPurchasePrice]: "Borrower Estimated Purchase Price",
    [QueueColumnNames.CashCloseType]: "Cash Close Type",
    [QueueColumnNames.ClosingDate]: "COE Date",
    [QueueColumnNames.ClosingPackageDueBy]: "Closing Package Due",
    [QueueColumnNames.ConditionsPreReadyToReview]: "Open, Requested, and Received Conditions",
    [QueueColumnNames.ConditionResponseDue]: "Condition Response Due",
    [QueueColumnNames.ConditionsReadyToReview]: "Ready To Review Conditions",
    [QueueColumnNames.CreditErrorMessages]: "Credit Error Messages",
    [QueueColumnNames.CreditReportDate]: "Credit Report Date",
    [QueueColumnNames.CreditScore]: "Credit Score",
    [QueueColumnNames.DisbursementDate]: "Disbursement Date",
    [QueueColumnNames.InitialCDDueBy]: "Initial CD Due",
    [QueueColumnNames.InitialLEDueBy]: "Initial LE Due",
    [QueueColumnNames.IntentToProceedReceipt]: "Intent To Proceed Receipt",
    [QueueColumnNames.IsArchived]: "Is Archived",
    [QueueColumnNames.LeadNumber]: "Lead #",
    [QueueColumnNames.LoanID]: "Loan #",
    [QueueColumnNames.LoanApplicationID]: "App #",
    [QueueColumnNames.LoanStatusDate]: "Loan Status Date",
    [QueueColumnNames.LoanStatus]: "Loan Status",
    [QueueColumnNames.LoanDecision]: "Loan Decision",
    [QueueColumnNames.LoanDecisionDate]: "Loan Decision Date",
    [QueueColumnNames.MessageResponseDue]: "Message Response Due",
    [QueueColumnNames.MessagesToReview]: "Messages To Review",
    [QueueColumnNames.NOIADue]: "NOIA Due On",
    [QueueColumnNames.NOIATimestamp]: "NOIA Date",
    [QueueColumnNames.NOIAFollowup]: "NOIA Follow Up",
    [QueueColumnNames.OutstandingDocuments]: "Outstanding Document Conditions",
    [QueueColumnNames.PostClosingPrepDueBy]: "Post Closing Prep Due",
    [QueueColumnNames.PropertyState]: "Property State",
    [QueueColumnNames.PreAppAANDue]: "Pre App AAN Due",
    [QueueColumnNames.PreAppAANSent]: "Pre App AAN Sent",
    [QueueColumnNames.PreAppAANReason]: "Pre App AAN Reason",
    [QueueColumnNames.PreAppUWDue]: "Pre App UW Due",
    [QueueColumnNames.RegistrationDate]: "Registration Date",
    [QueueColumnNames.RevisedLEDueBy]: "Revised LE Due",
    [QueueColumnNames.RevisedLESent]: "Revised LE Sent",
    [QueueColumnNames.RegBCreditDecisionDue]: "Reg B Credit Decision Due",
    [QueueColumnNames.ServicingTransferDueBy]: "Servicing Transfer Due",
    [QueueColumnNames.UWTaskCount]: "# of UW Tasks",
    [QueueColumnNames.UWTaskFirstDueBy]: "UW Task Due",
  };
  return tableColNames[k] || "Unknown";
};

export enum QueueCategory {
  Prospect = "Prospect",
  PreApplications = "PreApplications",
  RESPAComplete = "RESPA Complete",
}

// TODO: Replace QueueRole instances with {Role} from @eave/types/api/claims.ts
export enum QueueRole {
  LoanManager = "Loan Managers",
  Underwriter = "Underwriters",
}

export enum AppStage {
  Processing = "Processing",
  Closing = "Closing",
  PostClosing = "PostClosing",
}

/*
 * Functions
 */

export const formatDateOrDatetime = (t: string) => {
  if (isValidDate(t)) {
    return moment(t).format("MM/DD/YY");
  }

  return formatDateTimeString(t);
};

export function getColorForCurrentCategory(category: QueueCategory): string {
  if (category === QueueCategory.PreApplications) {
    return styles.accentPreapplication;
  }
  if (category === QueueCategory.RESPAComplete) {
    return styles.accentRespaComplete;
  }
  return "";
}

// A loan application is RESPA Complete if it has a purchased_property_received_date
// This helper func adds semantics to this check, instead of just expecting devs to know this fact
export function isRESPAComplete(app: TaskingAppShape): Boolean {
  return app.purchased_property_received_date !== null;
}

// `/queues` has tag links to jump to another spot in the same page
// This helper converts "hello world" -> "hello_world" (enabling href="#hello_world")
export function uniqueIDFromQueueName(queueType: string): string {
  // Filter out spaces, remove brackets and stuff
  return queueType
    .toLowerCase()
    .replace(/ /g, "_")
    .replace(/\[/g, "")
    .replace(/\]/g, "")
    .replace(/\(/g, "")
    .replace(/\)/g, "");
}

// Helper func: Returns apps that are in the PreApplication state
export function filterPreApps(apps: TaskingAppShape[]): TaskingAppShape[] {
  return apps.filter(a => !isRESPAComplete(a));
}

// Helper func: Returns apps that are in the RESPA Complete state
export function filterRESPACompleteApps(apps: TaskingAppShape[]): TaskingAppShape[] {
  return apps.filter(a => isRESPAComplete(a));
}

// Helper func: sorts 'apps' by 'dateProperty', with the oldest first
export function sortOldestFirst(apps: TaskingAppShape[], dateProperty: string) {
  return (
    apps
      // .filter(a => a.tasks.length > 0)
      .sort((appOne, appTwo) => {
        const a = moment(appOne[dateProperty]);
        const b = moment(appTwo[dateProperty]);

        if (a.isSame(b)) {
          return 0;
        }

        return a.isBefore(b) ? -1 : 1;
      })
  );
}

export const nextTasksSorted = (nextTasks): TaskShape[] =>
  nextTasks.sort((t1, t2) => {
    const compareClosingDates = compareTimestampOrNull(
      t1.loan_info.closing_date,
      t2.loan_info.closing_date,
    );
    const compareDueDates = compareTimestampOrNull(
      t1.next_task.resolved_due_date,
      t2.next_task.resolved_due_date,
    );

    if (compareClosingDates !== 0) {
      return compareClosingDates;
    }

    return compareDueDates;
  });
